import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import Box from 'shopper/components/Box';
import Button from 'shopper/components/Button';
import Heading from 'shopper/components/Heading';
import Text from 'shopper/components/Text';

import Image from 'components/Image';

import useDialog from 'hooks/useDialog';

import { isLastItem } from 'lib/array';
import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';

import { useUser } from 'providers/UserProvider';

import APP from 'constants/app';
import LINKS from 'constants/links';

const LoginDialog = dynamic(() => import('components/LoginDialog'), {
  ssr: false,
});

const KEY_ITEMS = [
  {
    icon: 'price-tag.svg',
    title: 'Preço baixo',
    text: 'Reunimos as melhores ofertas da internet, que são moderada uma a uma pela nossa equipe de especialistas.',
  },
  {
    icon: 'coupon.svg',
    title: 'Cupons de desconto',
    text: 'Também disponibilizamos cupons promocionais para você economizar ainda mais!',
  },
  {
    icon: 'community-like.svg',
    title: 'Comunidade',
    text: 'Uma rede social feita com a ajuda de compradores como você.',
  },
  {
    icon: 'security-locker.svg',
    title: 'Segurança',
    text: 'Verificamos todas as lojas para proporcionar a melhor experiencia de compra.',
  },
];

const HowPromobitWorks = forwardRef(({ className, gaEventCategory }, ref) => {
  const { showDialog } = useDialog();
  const { isLoggedIn } = useUser();

  const onButtonClick = () => {
    sendEvent({ category: gaEventCategory, action: 'join_community' });
    showDialog(LoginDialog);
  };

  return (
    <Box
      ref={ref}
      className={twMerge(
        'border-t-3 border-solid border-primary-300 p-4',
        className
      )}
    >
      <Heading as="span" size="size4">
        {placeholder('titles.whatIsPromobit')}
      </Heading>
      <Text className="mb-4 mt-2">
        {placeholder('descriptions.whatIsPromobitShort')}
      </Text>
      {KEY_ITEMS.map(({ icon, title, text }, index) => (
        <div
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          className={twJoin(
            'flex items-start border-t border-neutral-high-200 pt-4 dark:border-neutral-low-300',
            !isLastItem(KEY_ITEMS, index) && 'pb-4'
          )}
        >
          <div className="flex rounded-full bg-primary-300 p-3">
            <Image
              alt={title}
              height={20}
              src={`${APP.LOCAL_IMAGE_PATH}/features/how-promobit-works/${icon}`}
              width={20}
              aria-hidden
            />
          </div>
          <div className="flex flex-col pl-4">
            <Heading as="span" size="size6">
              {title}
            </Heading>
            <Text className="mt-2" size="size2">
              {text}
            </Text>
          </div>
        </div>
      ))}
      {isLoggedIn ? (
        <Button
          as="a"
          className="mt-6"
          href={LINKS.PROMOBIT_WHAT_IS}
          size="size2"
          full
        >
          {placeholder('actions.knowMoreAboutPromobit')}
        </Button>
      ) : (
        <Button className="mt-6" size="size2" full onClick={onButtonClick}>
          {placeholder('actions.groupSignup')}
        </Button>
      )}
    </Box>
  );
});

HowPromobitWorks.displayName = 'HowPromobitWorks';

HowPromobitWorks.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  gaEventCategory: PropTypes.string.isRequired,
};

export default HowPromobitWorks;
